import { createApi } from '@reduxjs/toolkit/query/react'
import {
  Content,
  ContentMetadataResponse,
  DeleteContentReq,
  DeleteContentResp,
  ListAnnotatedContentReq,
  ListAnnotatedContentResp,
  QueryContentResp,
} from '../types/models/content'
import {
  DELETE_CONTENT_URL,
  GET_CONTENT_URL,
  GET_RANDOM_CONTENT_URL,
  GET_SINGLE_CONTENT_URL,
  GET_SOURCES_URL,
  QUERY_CONTENT_URL,
  GENERATE_PCA_URL,
} from '../constants'
import baseQueryWithReauth from './baseQuery'
import { SampleContentResp } from '../types/api/responses'
import { apiTags } from './apiTags'
import { Filter } from '../types/common'

export const contentApi = createApi({
  reducerPath: 'contentApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Content, apiTags.Metadata, apiTags.ContentImage],

  endpoints: (builder) => ({
    fetchAnnotatedContent: builder.query<ListAnnotatedContentResp, ListAnnotatedContentReq>({
      query: (params) => {
        // Create a new params object without tag_id
        const { tag_id, ...restParams } = params

        // Convert tag_id array into URLSearchParams format
        const searchParams = new URLSearchParams()

        // Add all other params
        Object.entries(restParams).forEach(([key, value]) => {
          if (key === 'metadata_filter' && value) {
            searchParams.append(key, JSON.stringify(value))
          } else if (value !== undefined) {
            searchParams.append(key, value.toString())
          }
        })

        // Add each tag_id as a separate parameter
        if (tag_id?.length) {
          tag_id.forEach((id) => searchParams.append('tag_id', id))
        }

        return {
          url: GET_CONTENT_URL,
          params: searchParams,
        }
      },
      transformResponse: (response: ListAnnotatedContentResp) => {
        // Transform the new points-based structure to include legacy min/max properties
        const transformedContent = response.content.map((content) => ({
          ...content,
          annotation:
            content.annotation?.map((annotation) => ({
              ...annotation,
              metadata: {
                ...annotation.metadata,
                bounding_boxes:
                  annotation.metadata?.bounding_boxes?.map((box) => {
                    if (box.points && box.points.length === 8) {
                      // Points from backend are percentages (0-1), convert to pixel coordinates for UI
                      // Extract x and y coordinates from points array [x1,y1, x2,y2, x3,y3, x4,y4]
                      const pixelPoints = [
                        box.points[0] * content.width, // x1
                        box.points[1] * content.height, // y1
                        box.points[2] * content.width, // x2
                        box.points[3] * content.height, // y2
                        box.points[4] * content.width, // x3
                        box.points[5] * content.height, // y3
                        box.points[6] * content.width, // x4
                        box.points[7] * content.height, // y4
                      ]

                      // Calculate min/max from the converted pixel coordinates
                      const xValues = [
                        pixelPoints[0],
                        pixelPoints[2],
                        pixelPoints[4],
                        pixelPoints[6],
                      ]
                      const yValues = [
                        pixelPoints[1],
                        pixelPoints[3],
                        pixelPoints[5],
                        pixelPoints[7],
                      ]
                      const xmin = Math.min(...xValues)
                      const xmax = Math.max(...xValues)
                      const ymin = Math.min(...yValues)
                      const ymax = Math.max(...yValues)

                      return {
                        ...box,
                        points: pixelPoints, // Now in pixel coordinates for UI
                        xmin,
                        xmax,
                        ymin,
                        ymax,
                      }
                    } else if (
                      box.xmin !== undefined &&
                      box.xmax !== undefined &&
                      box.ymin !== undefined &&
                      box.ymax !== undefined &&
                      (!box.points || box.points.length !== 8)
                    ) {
                      // Legacy: Min/max exist but points don't - create pixel points from min/max
                      const points = [
                        box.xmin, // x1 (top-left)
                        box.ymin, // y1 (top-left)
                        box.xmax, // x2 (top-right)
                        box.ymin, // y2 (top-right)
                        box.xmax, // x3 (bottom-right)
                        box.ymax, // y3 (bottom-right)
                        box.xmin, // x4 (bottom-left)
                        box.ymax, // y4 (bottom-left)
                      ]

                      return {
                        ...box,
                        points, // Pixel coordinates for UI
                      }
                    }
                    return box
                  }) || [],
                oriented_bounding_boxes:
                  annotation.metadata?.oriented_bounding_boxes?.map((box) => {
                    if (box.points && box.points.length === 8) {
                      // Points from backend are percentages (0-1), convert to pixel coordinates for UI
                      // Extract x and y coordinates from points array [x1,y1, x2,y2, x3,y3, x4,y4]
                      const pixelPoints = [
                        box.points[0] * content.width, // x1
                        box.points[1] * content.height, // y1
                        box.points[2] * content.width, // x2
                        box.points[3] * content.height, // y2
                        box.points[4] * content.width, // x3
                        box.points[5] * content.height, // y3
                        box.points[6] * content.width, // x4
                        box.points[7] * content.height, // y4
                      ]

                      // Calculate min/max from the converted pixel coordinates
                      const xValues = [
                        pixelPoints[0],
                        pixelPoints[2],
                        pixelPoints[4],
                        pixelPoints[6],
                      ]
                      const yValues = [
                        pixelPoints[1],
                        pixelPoints[3],
                        pixelPoints[5],
                        pixelPoints[7],
                      ]
                      const xmin = Math.min(...xValues)
                      const xmax = Math.max(...xValues)
                      const ymin = Math.min(...yValues)
                      const ymax = Math.max(...yValues)

                      return {
                        ...box,
                        points: pixelPoints, // Now in pixel coordinates for UI
                        xmin,
                        xmax,
                        ymin,
                        ymax,
                      }
                    } else if (
                      box.xmin !== undefined &&
                      box.xmax !== undefined &&
                      box.ymin !== undefined &&
                      box.ymax !== undefined &&
                      (!box.points || box.points.length !== 8)
                    ) {
                      // Legacy: Min/max exist but points don't - create pixel points from min/max
                      const points = [
                        box.xmin, // x1 (top-left)
                        box.ymin, // y1 (top-left)
                        box.xmax, // x2 (top-right)
                        box.ymin, // y2 (top-right)
                        box.xmax, // x3 (bottom-right)
                        box.ymax, // y3 (bottom-right)
                        box.xmin, // x4 (bottom-left)
                        box.ymax, // y4 (bottom-left)
                      ]

                      return {
                        ...box,
                        points, // Pixel coordinates for UI
                      }
                    }
                    return box
                  }) || [],
              },
            })) || [],
        }))

        return {
          ...response,
          content: transformedContent,
        }
      },
      providesTags: (result) => [
        { type: apiTags.Content, id: 'LIST' },
        ...(result?.content.map(({ id }) => ({ type: apiTags.Content, id })) || []),
      ],
      keepUnusedDataFor: 300,
    }),

    deleteContent: builder.mutation<DeleteContentResp, DeleteContentReq>({
      query: (params) => ({
        url: DELETE_CONTENT_URL,
        method: 'DELETE',
        body: params,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: apiTags.Content, id: 'LIST' },
        ...(arg.content_ids?.map((id) => ({ type: apiTags.Content, id })) || []),
        ...(arg.content_ids?.map((id) => ({
          type: apiTags.ContentImage,
          id,
        })) || []),
        { type: apiTags.Metadata, id: arg.project_id },
        { type: apiTags.Metadata, id: 'LIST' },
        { type: apiTags.Annotation, id: 'LIST' },
        ...(arg.content_ids?.map((id) => ({ type: apiTags.Annotation, id })) || []),
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.AnnotationStats, id: arg.dataset_id },
        { type: apiTags.Tag, id: 'LIST' },
        { type: apiTags.PredictionList, id: 'LIST' },
        { type: apiTags.PredictionStats, id: arg.model_id },
      ],
    }),

    queryContent: builder.query<
      QueryContentResp,
      {
        filters: Filter[]
        limit?: number
        page?: number
        sort_key?: string
        sort_val?: number
        operator?: 'and' | 'or' | 'nor'
      }
    >({
      query: (params) => ({
        url: QUERY_CONTENT_URL,
        method: 'POST',
        body: params,
      }),
      providesTags: (result) => [
        { type: apiTags.Content, id: 'LIST' },
        ...(result?.content || []).map(({ id }: { id: string }) => ({
          type: apiTags.Content,
          id,
        })),
      ],
      keepUnusedDataFor: 300,
    }),

    sampleContent: builder.query<
      SampleContentResp,
      {
        project_id: string
        count: number
        filter_annotated?: boolean
        metadata_filter?: string
        source?: string[]
      }
    >({
      query: (params) => ({
        url: GET_RANDOM_CONTENT_URL,
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.Content, id: 'SAMPLE_LIST' },
        ...(result?.content || []).map(({ id }) => ({
          type: apiTags.Content,
          id,
        })),
      ],
    }),

    getContentMetadata: builder.query<ContentMetadataResponse, { project_id: string }>({
      query: ({ project_id, ...params }) => ({
        url: GET_SOURCES_URL,
        params: { ...params, project_id },
      }),
      providesTags: (result, error, { project_id }) => [
        { type: apiTags.Metadata, id: project_id },
        { type: apiTags.Metadata, id: 'LIST' },
      ],
    }),

    viewContent: builder.query<
      Content,
      {
        ContentID: string
        project_id: string
        dataset_id?: string
      }
    >({
      query: ({ ContentID, ...params }) => ({
        url: `${GET_SINGLE_CONTENT_URL}/${ContentID}`,
        params,
      }),
      providesTags: (result, error, { ContentID }) => [
        { type: apiTags.Content, id: ContentID },
        { type: apiTags.ContentImage, id: ContentID },
      ],
      keepUnusedDataFor: 300,
    }),

    getContentImage: builder.query<
      string,
      {
        ContentID: string
        project_id: string
        dataset_id?: string
        _t?: number // Cache busting parameter
      }
    >({
      query: ({ ContentID, _t, ...params }) => ({
        url: `${GET_SINGLE_CONTENT_URL}/${ContentID}`,
        params: { ...params, image: true, ...((_t && { _t }) || {}) },
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorData = await response.json()
            return Promise.reject(errorData)
          }
          return response.blob()
        },
      }),
      transformResponse: async (blob: Blob) => URL.createObjectURL(blob),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify(queryArgs)
      },
      transformErrorResponse: (response: { status: number; data: { message?: string } }) => {
        return {
          status: response.status,
          message: response.data?.message || 'Failed to load image',
        }
      },
      keepUnusedDataFor: 300,
      forceRefetch: ({ currentArg, previousArg, endpointState }) => {
        // Force refetch when cache is invalidated
        return !endpointState?.data
      },
      providesTags: (result, error, { ContentID }) => [
        { type: apiTags.ContentImage, id: ContentID },
      ],
    }),

    generatePCA: builder.mutation<void, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `${GENERATE_PCA_URL}/${projectId}/vector/pca`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: apiTags.Content, id: 'LIST' },
        { type: apiTags.Metadata, id: projectId },
      ],
    }),
  }),
})

export const {
  useFetchAnnotatedContentQuery,
  useDeleteContentMutation,
  useQueryContentQuery,
  useSampleContentQuery,
  useViewContentQuery,
  useGetContentImageQuery,
  useGetContentMetadataQuery,
  useGeneratePCAMutation,
} = contentApi

export const {
  util: { invalidateTags },
} = contentApi
